import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { WalletWidget } from "../components/WalletWidget";
import { StatCard } from "../components/StatCard";
import {
  Users,
  UserCheck,
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wallet,
  Briefcase,
  Play,
  Square,
} from "lucide-react";
import { toast } from "sonner";

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = (user?.role_level || 0) >= 4;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, attendanceRes, leaveRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/attendance/today`, { headers }),
        axios.get(`${API}/leaves/balance`, { headers }),
      ]);

      setStats(statsRes.data);
      setAttendance(attendanceRes.data);
      setLeaveBalance(leaveRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/attendance/check-in`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.type === "needs_request") {
        toast.warning("Check-in window has closed. Please use the Attendance page to submit a late request.");
      } else if (res.data.type === "too_early") {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message || "Checked in successfully!");
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in");
    }
  };

  const handleCheckOut = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/attendance/check-out`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Checked out successfully!");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check out");
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Admin Stats */}
      {isAdmin && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Employees"
            value={stats.total_employees}
            icon={Users}
            variant="primary"
            testId="stat-total-employees"
          />
          <StatCard
            title="Active Employees"
            value={stats.active_employees}
            icon={UserCheck}
            variant="success"
            testId="stat-active-employees"
          />
          <StatCard
            title="Pending Approvals"
            value={stats.pending_approvals}
            icon={AlertCircle}
            variant={stats.pending_approvals > 0 ? "warning" : "default"}
            testId="stat-pending-approvals"
          />
          <StatCard
            title="On Leave Today"
            value={stats.on_leave_today}
            icon={Calendar}
            variant="default"
            testId="stat-on-leave"
          />
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Wallet Widget - Takes 1 column */}
        <WalletWidget className="lg:col-span-1" />

        {/* Right Section - Takes 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Attendance Card */}
          <Card data-testid="attendance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Today&apos;s Attendance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-1">Check In</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="check-in-time">
                      {formatTime(attendance?.check_in)}
                    </p>
                  </div>
                  <div className="h-12 w-px bg-slate-200" />
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-1">Check Out</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="check-out-time">
                      {formatTime(attendance?.check_out)}
                    </p>
                  </div>
                  {attendance?.work_hours > 0 && (
                    <>
                      <div className="h-12 w-px bg-slate-200" />
                      <div className="text-center">
                        <p className="text-sm text-slate-500 mb-1">Work Hours</p>
                        <p className="text-2xl font-bold text-success" data-testid="work-hours">
                          {attendance.work_hours}h
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex gap-3">
                  {!attendance?.check_in ? (
                    <Button onClick={handleCheckIn} className="gap-2" data-testid="check-in-btn">
                      <Play className="w-4 h-4" /> Check In
                    </Button>
                  ) : !attendance?.check_out ? (
                    <Button onClick={handleCheckOut} variant="outline" className="gap-2" data-testid="check-out-btn">
                      <Square className="w-4 h-4" /> Check Out
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Day Complete</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Leave Balance & Performance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Leave Balance */}
            <Card data-testid="leave-balance-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="w-5 h-5 text-primary" />
                  Leave Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Casual Leave</p>
                    <p className="text-xl font-bold text-slate-900">{leaveBalance?.casual_leave || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Sick Leave</p>
                    <p className="text-xl font-bold text-slate-900">{leaveBalance?.sick_leave || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Earned Leave</p>
                    <p className="text-xl font-bold text-slate-900">{leaveBalance?.earned_leave || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Comp Off</p>
                    <p className="text-xl font-bold text-slate-900">{leaveBalance?.comp_off || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Score */}
            <Card data-testid="performance-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Performance Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-slate-100"
                        strokeWidth="10"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                      />
                      <circle
                        className="text-primary"
                        strokeWidth="10"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                        strokeDasharray={`${(user?.performance_score || 0) * 2.51} 251`}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-900" data-testid="performance-score">
                          {user?.performance_score || 0}
                        </p>
                        <p className="text-xs text-slate-500">/100</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-slate-500 mt-4">
                  {getPerformanceBand(user?.performance_score || 0)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Admin Financial Overview */}
      {isAdmin && stats && (
        <Card data-testid="financial-overview-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Financial Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">Total Wallet Balances</p>
                <p className="text-2xl font-bold text-slate-900">
                  ₹{(stats.total_wallet_balance || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/5 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">Locked Balances</p>
                <p className="text-2xl font-bold text-slate-900">
                  ₹{(stats.locked_balance || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-gradient-to-br from-success/10 to-success/5 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">Unlocking This Month</p>
                <p className="text-2xl font-bold text-slate-900">
                  ₹{(stats.unlocking_this_month || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const getPerformanceBand = (score) => {
  if (score >= 90) return "⭐ Outstanding";
  if (score >= 75) return "⭐ Excellent";
  if (score >= 60) return "⭐ Good";
  if (score >= 45) return "⚠️ Needs Improvement";
  return "🚨 PIP Required";
};
